import { useEffect, useRef } from 'react';
import { useStore } from '../store/useStore.js';

const W = 180;
const H = 120;
const PAD = 12;

type Bounds = { minX: number; maxX: number; minY: number; maxY: number };

// Mapeamento mundo→minimapa com ESCALA UNIFORME (mesma em X e Y), centralizado.
// Mantém a geometria fiel ao canvas principal — sem distorção.
function makeProjection(b: Bounds) {
  const bw = b.maxX - b.minX || 1;
  const bh = b.maxY - b.minY || 1;
  const scale = Math.min((W - PAD * 2) / bw, (H - PAD * 2) / bh);
  const offX = (W - bw * scale) / 2;
  const offY = (H - bh * scale) / 2;
  return {
    scale,
    toMini(wx: number, wy: number) {
      return { mx: offX + (wx - b.minX) * scale, my: offY + (wy - b.minY) * scale };
    },
    toWorld(mx: number, my: number) {
      return { wx: b.minX + (mx - offX) / scale, wy: b.minY + (my - offY) / scale };
    },
  };
}

export function Minimap() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draggingRef = useRef(false);
  const projRef = useRef<ReturnType<typeof makeProjection> | null>(null);
  const countRef = useRef<HTMLSpanElement>(null);

  // Loop rAF: a física muta posições in-place sem novo snapshot do store, então
  // efeito-por-dependência fica defasado. Lendo getState() por frame o minimapa
  // acompanha posição dos nós E zoom da câmera em tempo real.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let raf = 0;
    function draw() {
      raf = requestAnimationFrame(draw);
      if (!ctx) return;
      const state = useStore.getState();
      const fp = state.focusedParentId;
      const visibleNodes = state.nodes.filter((n) => (n.parentId ?? null) === (fp ?? null));
      const visibleIds = new Set(visibleNodes.map((n) => n.id));
      const visibleLinks = state.links.filter((l) => visibleIds.has(l.source) && visibleIds.has(l.target));
      const cam = state.cam;

      if (countRef.current) countRef.current.textContent = String(visibleNodes.length);

      // Bounds incluem o viewport atual — assim o retângulo nunca "sai" do mapa.
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const n of visibleNodes) {
        if (n.x < minX) minX = n.x;
        if (n.x > maxX) maxX = n.x;
        if (n.y < minY) minY = n.y;
        if (n.y > maxY) maxY = n.y;
      }
      if (visibleNodes.length === 0) { minX = -400; maxX = 400; minY = -300; maxY = 300; }
      const vw = window.innerWidth;
      const vh = window.innerHeight - 54; // topbar
      const halfW = (vw / 2) / cam.zoom;
      const halfH = (vh / 2) / cam.zoom;
      const vMinX = -cam.x - halfW, vMaxX = -cam.x + halfW;
      const vMinY = -cam.y - halfH, vMaxY = -cam.y + halfH;
      const b: Bounds = {
        minX: Math.min(minX, vMinX) - 60,
        maxX: Math.max(maxX, vMaxX) + 60,
        minY: Math.min(minY, vMinY) - 60,
        maxY: Math.max(maxY, vMaxY) + 60,
      };
      const proj = makeProjection(b);
      projRef.current = proj;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(4, 8, 16, 0.85)';
      ctx.fillRect(0, 0, W, H);

      // Links.
      const byId = new Map(visibleNodes.map((n) => [n.id, n]));
      ctx.strokeStyle = 'rgba(0,245,255,0.18)';
      ctx.lineWidth = 0.6;
      for (const lk of visibleLinks) {
        const a = byId.get(lk.source);
        const c = byId.get(lk.target);
        if (!a || !c) continue;
        const pa = proj.toMini(a.x, a.y);
        const pc = proj.toMini(c.x, c.y);
        ctx.beginPath();
        ctx.moveTo(pa.mx, pa.my);
        ctx.lineTo(pc.mx, pc.my);
        ctx.stroke();
      }

      // Nodes — raio acompanha o tamanho real (escala uniforme do mapa).
      for (const n of visibleNodes) {
        const { mx, my } = proj.toMini(n.x, n.y);
        const r = Math.max(1.4, Math.min(5, (n.size || 22) * proj.scale));
        ctx.fillStyle = n.color || '#00f5ff';
        ctx.globalAlpha = n.id === state.selectedId ? 1 : 0.85;
        ctx.beginPath();
        ctx.arc(mx, my, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Viewport — retângulo do que está visível no canvas principal.
      const p1 = proj.toMini(vMinX, vMinY);
      const p2 = proj.toMini(vMaxX, vMaxY);
      ctx.strokeStyle = 'rgba(0,245,255,0.7)';
      ctx.lineWidth = 1;
      ctx.strokeRect(p1.mx, p1.my, p2.mx - p1.mx, p2.my - p1.my);
      ctx.fillStyle = 'rgba(0,245,255,0.08)';
      ctx.fillRect(p1.mx, p1.my, p2.mx - p1.mx, p2.my - p1.my);
    }
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  function recenter(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    const proj = projRef.current;
    if (!canvas || !proj) return;
    const rect = canvas.getBoundingClientRect();
    const { wx, wy } = proj.toWorld(clientX - rect.left, clientY - rect.top);
    const state = useStore.getState();
    state.setCamera({ x: -wx, y: -wy, zoom: state.cam.zoom });
  }

  function pickMiniNode(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    const proj = projRef.current;
    if (!canvas || !proj) return null;
    const rect = canvas.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const state = useStore.getState();
    const fp = state.focusedParentId;
    for (const n of state.nodes) {
      if ((n.parentId ?? null) !== (fp ?? null)) continue;
      const p = proj.toMini(n.x, n.y);
      if (Math.hypot(p.mx - mx, p.my - my) < 6) return n.id;
    }
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: 60,
        bottom: 18,
        width: W,
        height: H + 22,
        zIndex: 60,
        background: 'rgba(2, 6, 16, 0.78)',
        border: '1px solid rgba(0,245,255,0.18)',
        borderRadius: 10,
        backdropFilter: 'blur(14px)',
        boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 10px', fontSize: 10, color: 'var(--muted)',
        letterSpacing: '0.08em', textTransform: 'uppercase',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <span>Mapa</span>
        <span ref={countRef} style={{ opacity: 0.6 }} />
      </div>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: W, height: H, cursor: 'crosshair' }}
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          const state = useStore.getState();
          const nodeId = pickMiniNode(e.clientX, e.clientY);
          if (nodeId) {
            state.setSelected(nodeId);
            const node = state.nodes.find((n) => n.id === nodeId);
            if (node) state.setCamera({ x: -node.x, y: -node.y, zoom: state.cam.zoom });
          } else {
            draggingRef.current = true;
            recenter(e.clientX, e.clientY);
          }
        }}
        onMouseMove={(e) => {
          if (draggingRef.current) recenter(e.clientX, e.clientY);
        }}
        onMouseUp={() => { draggingRef.current = false; }}
        onMouseLeave={() => { draggingRef.current = false; }}
      />
    </div>
  );
}
